import { DrawerFooter } from '@client/components/_core/layout/DrawerFooter';
import { OverlayPage } from '@client/components/_core/layout/OverlayPage';
import { PageController } from '@client/components/_core/layout/PageController';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useFormDirty } from '@client/hooks/forms/useFormDirty';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { type RouterInputs, trpc } from '@client/trpc/client';
import { createFileRoute } from '@tanstack/react-router';
import { Form, Input } from 'antd';
import { useId } from 'react';

export const Route = createFileRoute('/console/$orgId/configuration/claims-products/product-types/add')({
    staticData: {
        presentation: 'overlay',
        metadata: {
            title: 'New product type',
        },
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <ProductTypeAdd />
        </PageController>
    );
}

type ProductType = RouterInputs['productType']['createProductType'];

const FormItem = Form.Item<ProductType>;

function ProductTypeAdd() {
    const formId = useId();
    const { trpcUtils } = useTrpc();
    const { mutate } = trpc.productType.createProductType.useMutation();
    const navigate = useNavigate();

    const [form] = Form.useForm<ProductType>();

    const formDirty = useFormDirty();

    return (
        <OverlayPage
            isDirty={formDirty.isDirty}
            title="New product type"
            afterClose={() => {
                form.resetFields();
                void trpcUtils.productType.invalidate();
                navigate({ moveInMatches: -1 });
            }}
            footer={({ requestClose }) => <DrawerFooter onCancel={requestClose} formId={formId} />}
        >
            {({ onClose }) => (
                <Form
                    id={formId}
                    layout="vertical"
                    form={form}
                    {...formDirty.formProps}
                    onFinish={(values) => {
                        message.loading('Saving...');

                        mutate(
                            {
                                name: values.name,
                            },
                            {
                                onSuccess() {
                                    message.success('Saved.');
                                    onClose();
                                },
                                onError(error) {
                                    if (error.data?.errorId === 'productType.alreadyExists') {
                                        message.destroy();

                                        form.setFields([
                                            {
                                                name: 'name',
                                                errors: ['This name is already in use'],
                                            },
                                        ]);

                                        return;
                                    }

                                    errorMessage.show(error);
                                },
                            },
                        );
                    }}
                >
                    <FormItem label="Name" name="name" rules={[{ required: true }]}>
                        <Input />
                    </FormItem>
                </Form>
            )}
        </OverlayPage>
    );
}
